<template>
  <div class="invite-user-list">
    <base-review-member-list-item
      v-for="{
        researcher,
        reviewMemberInvite,
      } in researcherOrInviteToShow"
      :key="getListItemKey({ researcher, reviewMemberInvite })"
      :researcher="researcher"
      :review-member-invite="reviewMemberInvite"
      class="invite-user-list-item"
    >
      <!-- Extra metadata about the user to the right of the users name -->
      <template #after-name>
        <span
          v-if="reviewMemberInvite"
          class="text-body-2 grey--text"
        >
          Pending
        </span>
        <span
          v-else-if="researcherIsLead(researcher)"
          class="text-body-2 grey--text"
        >
          Lead Reviewer
        </span>
      </template>

      <!-- Actions section -->
      <template v-if="editable" #right>
        <v-tooltip top>
          <template #activator="{ on }">
            <!-- Delete button (which is disabled for lead reviewers) -->
            <div
              v-if="
                reviewMemberInvite ||
                researcherIsPartOfReview(researcher)
              "
              v-on="on"
            >
              <v-btn
                :disabled="researcherIsLead(researcher)"
                icon
                @click="onDelete({ researcher, reviewMemberInvite })"
              >
                <v-icon class="material-icons-outlined" color="error">
                  delete
                </v-icon>
              </v-btn>
            </div>

            <!-- Invite user button -->
            <v-btn
              v-else
              icon
              @click="$emit('invite', researcher)"
              v-on="on"
            >
              <v-icon color="primary">add_circle</v-icon>
            </v-btn>
          </template>

          <!-- Tooltip content -->
          <span v-if="researcherIsLead(researcher)">
            Cannot remove the lead reviewer
          </span>
          <span v-else-if="researcherIsPartOfReview(researcher)">
            Remove from review
          </span>
          <span v-else-if="reviewMemberInvite">Revoke invite</span>
          <span v-else>Invite to review</span>
        </v-tooltip>
      </template>
    </base-review-member-list-item>
  </div>
</template>

<script>
import BaseReviewMemberListItem from './BaseReviewMemberListItem';
import {
  ReviewMember,
  ReviewMemberInvite,
  Researcher,
} from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'InviteUserList',
  components: { BaseReviewMemberListItem },
  props: {
    searchInput: {
      type: String,
      default: null,
    },
    leadReviewer: {
      type: ReviewMember,
      default: null,
    },
    reviewers: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, ReviewMember),
    },
    invites: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, ReviewMemberInvite),
    },
    researcherSearchResults: {
      type: Array,
      default: () => [],
      validator: (val) => typeHelpers.isArrayOfType(val, Researcher),
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['invite', 'delete', 'revoke-invite'],
  computed: {
    researcherOrInviteToShow() {
      const results = [];
      if (this.searchInput) {
        // Find all reviewers that match the search input (by name).
        const reviewersSearch = this.findReviewersByNameOrEmail(
          this.searchInput
        ).map(({ researcher }) => ({ researcher }));
        results.push(...reviewersSearch);

        // Find all invites that match the search input (by email).
        const invitesSearch = this.findInvitesByEmail(
          this.searchInput
        ).map((reviewMemberInvite) => ({ reviewMemberInvite }));
        results.push(...invitesSearch);

        // Inject the wider researcher search results into the list if any.
        if (this.researcherSearchResults?.length > 0) {
          const nonReviewerResearchers = this.researcherSearchResults
            .filter(
              (researcher) =>
                !this.researcherIsPartOfReview(researcher)
            )
            .map((researcher) => ({
              researcher,
            }));
          results.push(...nonReviewerResearchers);
        }
      } else {
        // Show all reviewers if no search input is provided.
        const allReviewers = this.leadAndReviewers.map(
          (reviewer) => ({
            researcher: reviewer.researcher,
          })
        );
        const allInvites = this.invites.map((reviewMemberInvite) => ({
          reviewMemberInvite,
        }));
        results.push(...allReviewers);
        results.push(...allInvites);
      }

      return results;
    },
    leadAndReviewers() {
      if (this.leadReviewer) {
        return [this.leadReviewer, ...this.reviewers];
      }
      return this.reviewers;
    },
  },
  methods: {
    getListItemKey({ researcher, reviewMemberInvite }) {
      // Make sure the key changes on search.
      // Otherwise the list item will not be re-rendered.
      if (researcher) {
        return `${this.searchInput}-${researcher.id}`;
      } else if (reviewMemberInvite) {
        return `${this.searchInput}-${reviewMemberInvite.id}`;
      }
    },
    onDelete({ researcher, reviewMemberInvite }) {
      if (researcher) {
        this.$emit('delete', researcher);
      } else if (reviewMemberInvite) {
        this.$emit('revoke-invite', reviewMemberInvite);
      }
    },
    researcherIsPartOfReview(researcher) {
      return this.leadAndReviewers.some(
        (reviewer) => reviewer.researcher.id === researcher?.id
      );
    },
    researcherIsLead(researcher) {
      if (!this.leadReviewer) {
        return false;
      }
      return this.leadReviewer?.researcher.id === researcher?.id;
    },
    findReviewersByNameOrEmail(searchInput) {
      const searchInputLowerCase = searchInput.toLowerCase();
      return this.leadAndReviewers.filter(
        (reviewer) =>
          reviewer.researcher.name
            .toLowerCase()
            .includes(searchInputLowerCase) ||
          reviewer.researcher.email.includes(searchInputLowerCase)
      );
    },
    findInvitesByEmail(searchInput) {
      return this.invites.filter((invite) =>
        invite.emailAddress
          .toLowerCase()
          .includes(searchInput.toLowerCase())
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.invite-user-list {
  max-height: 300px;
  overflow-y: auto;
}
.invite-user-list-item:not(:last-child) {
  margin-bottom: 8px;
}
</style>
